import {EventEmitter, Injectable} from '@angular/core';
import {from, Observable, ObservableInput, of, Subscriber, Subscription, throwError} from 'rxjs';
import {
  connect,
  ConnectOptions,
  createLocalAudioTrack,
  CreateLocalTracksOptions,
  createLocalVideoTrack,
  LocalDataTrack,
  LocalTrack,
  LocalTrackPublication,
  RemoteAudioTrackPublication,
  RemoteDataTrackPublication,
  RemoteParticipant,
  RemoteVideoTrackPublication,
  Room,
  TrackPublication
} from 'twilio-video';
import {catchError, concatMap, map} from 'rxjs/operators';

import {
  NetworkQualityLevelChangedPayload,
  RemoteParticipantConnectionEventPayload,
  SpeakingEventPayload,
  TrackReceivedEventPayload
} from '@app/models/types';
import {AccessType, JsonLogAction, LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';

import {CurrentUserTracks} from '@app/modules/wrtc/models/twilio/current-user-tracks';
import {RemoteUserTracks} from '@app/modules/wrtc/models/twilio/remote-user-tracks';
import {TrackKind} from '@app/modules/wrtc/models/twilio/types';
import {RoomConnection} from '@app/modules/wrtc/models/room-connection';
import {LogService} from '@app/services/log.service';
import {LocalVideoTrack} from 'twilio-video/tsdef/LocalVideoTrack';
import {LocalAudioTrack} from 'twilio-video/tsdef/LocalAudioTrack';
import {DeviceMetadata} from '@app/modules/wrtc/models/device-metadata';
import {Store} from '@ngxs/store';
import {CurrentUserVideoOptions} from '@app/modules/wrtc/models/twilio/current-user-video-options';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {
  RemoteUserAdditionalInfoStateModel,
  RemoteUserInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {JsonLogService} from '@app/modules/shared/services/json-log.service';
import {UsersListService} from '@app/modules/shared/services/users-list.service';
import {HttpClient} from '@angular/common/http';
import {
  Service,
  UserDisconnectionHandlerService
} from '@app/modules/shared/services/user-disconnection-handler.service';
import {LocalAudioTrackPublication} from 'twilio-video/tsdef/LocalAudioTrackPublication';
import {Track} from 'twilio-video/tsdef/Track';


@Injectable()
export class TwilioService extends WRTCProviderService {
  private currentUserMediaWrapper: HTMLElement;
  private currentUserTracks = new CurrentUserTracks();
  public remoteParticipantsTrackById: { [key: number]: RemoteUserTracks };
  private remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel } = {};
  private currentUserId: number;
  private currentUserRole: AccessType;
  private room: Room;
  public token: string;
  public roomName: string;
  public publisherOptions: any;
  public currentUserVideoTrackOptions = new CurrentUserVideoOptions();
  public classroom: ClassroomStateModel;
  private shouldReconnect: boolean = false;

  private remoteUsersMedia: {[key: number]: {stream: MediaStream; source: MediaStreamAudioSourceNode; audioContext: AudioContext; analyzer: AnalyserNode}} = {};

  public loadedTracksByDeviceId: {
    [key: string]: LocalVideoTrack | LocalAudioTrack;
  } = {};

  public selectedSpeakerDeviceId: string | null = localStorage.getItem('audio_output_device_id') || null;

  private remoteParticipantConnectionStateChanged$: EventEmitter<RemoteParticipantConnectionEventPayload> = new EventEmitter();
  private audioTrackReceived$: EventEmitter<TrackReceivedEventPayload> = new EventEmitter();
  private videoTrackReceived$: EventEmitter<TrackReceivedEventPayload> = new EventEmitter();
  private dominantSpeakerChanged$: EventEmitter<number | null> = new EventEmitter();
  private localVideoProvidedChanged$: EventEmitter<boolean> = new EventEmitter();
  private lastReportedLocalVideoProvided: boolean = null;
  private localAudioProvidedChanged$: EventEmitter<boolean> = new EventEmitter();
  private lastReportedLocalAudioProvided: boolean = null;
  private localVoiceMeterIntervalRef: any = null;
  private localVoiceMeterAudioContext: AudioContext = null;


  constructor(
    private store: Store,
    private jsonLogService: JsonLogService,
    private usersListService: UsersListService,
    private userDisconnectionHandlerService: UserDisconnectionHandlerService,
    private http: HttpClient
  ) {
    super();
    LogService.writeLog(`TWILIO WRTC service is IInjected.`, LogLevel.DEBUG, LogResource.WRTC);
    this.remoteParticipantsTrackById = {};
    this.registerStoreChangeListeners();

    /* stop all DOM audio/video elements before page unload, to prevent from unnecessarily logs */
    window.addEventListener('beforeunload', () => {
      const videoElements: HTMLCollectionOf<HTMLVideoElement> = document.getElementsByTagName('video');
      const audioElements: HTMLCollectionOf<HTMLAudioElement> = document.getElementsByTagName('audio');

      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < videoElements.length; i++) {
        videoElements[i].pause();
      }
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < audioElements.length; i++) {
        audioElements[i].pause();
      }

      this.room?.disconnect();
    });
  }

  public clearServiceState() {
    this.stopLocalVoiceMeter();
    this.currentUserTracks?.disconnect();
    // stop loaded tracks
    Object.keys(this.loadedTracksByDeviceId).forEach(deviceId => {
      if (this.loadedTracksByDeviceId[deviceId].kind === TrackKind.AUDIO) {
        this.stopAudioTrack(deviceId);
      } else {
        this.detachAndStopVideoTrack(deviceId, true);
      }
    });

    this.currentUserTracks = new CurrentUserTracks();
    this.room = null;
    this.loadedTracksByDeviceId = {};
  }

  public registerStoreChangeListeners() {
    this.store.select(RemoteUsersInfoState).subscribe(({usersInfoById}: RemoteUserInfoStateModel) => {
      this.remoteUsers = usersInfoById;
    });
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
  }

  //Whenever a participant connects to a room, following steps are being taken
  public connectToRoom(token: string, id: number, roomName: string, accessType: AccessType): Observable<RoomConnection> {
    this.token = token;
    this.roomName = roomName;
    this.currentUserId = id;
    this.currentUserRole = accessType;
    this.publisherOptions = this.getPublisherOptions(accessType);
    return this.getConnectOptions(roomName, accessType).pipe(
      catchError((err) => {
        this.onConnectionErrorEventEmitter.emit(err);
        return throwError(err);
      }),
      concatMap((connectionOptions) => {
        return from(connect(token, connectionOptions)).pipe(
          catchError((err) => {
            this.onConnectionErrorEventEmitter.emit(err);
            return throwError(err);
          }),
          concatMap((room: Room) => {
            return this.onConnectToRoom(room).pipe(map((roomConnection: RoomConnection): RoomConnection => {
              this.onConnectedEventEmitter.emit(roomConnection);
              return roomConnection;
            }));
          })
        );
      })
    );
  }

  public disconnectFromRoom(): void {
    LogService.writeLog(`Disconnected form WRTC ROOM`, LogLevel.DEBUG, LogResource.WRTC);
    this.room?.disconnect();
    this.clearServiceState();
  }

  public reconnectToRoom(): Observable<RoomConnection> {
    if (this.isReconnecting) {
      return new Observable((subscriber: Subscriber<RoomConnection>): void => {
        const connectSubscription: Subscription = this.onConnectedEventEmitter.subscribe((roomConnection: RoomConnection): void => {
          connectSubscription.unsubscribe();
          subscriber.next(roomConnection);
          subscriber.complete();
        });
        const errorSubscription: Subscription = this.onConnectionErrorEventEmitter.subscribe((error: any): void => {
          errorSubscription.unsubscribe();
          subscriber.error(error);
          subscriber.complete();
        });
      });
    }

    if (this.isConnected) {
      this.shouldReconnect = true;
      return new Observable((subscriber: Subscriber<RoomConnection>): void => {
        const connectSubscription: Subscription = this.onConnectedEventEmitter.subscribe((roomConnection: RoomConnection): void => {
          connectSubscription.unsubscribe();
          this.shouldReconnect = false;
          subscriber.next(roomConnection);
          subscriber.complete();
        });
        const errorSubscription: Subscription = this.onConnectionErrorEventEmitter.subscribe((error: any): void => {
          errorSubscription.unsubscribe();
          subscriber.error(error);
          subscriber.complete();
        });

        this.room?.disconnect(); /* disconnect and wait for connection event */
        this.clearServiceState();
      });
    }

    this.room?.disconnect();
    this.clearServiceState();
    return this.connectToRoom(this.token, this.currentUserId, this.roomName, this.currentUserRole);
  }

  private onConnectToRoom(room: Room): Observable<RoomConnection> {
    if (this.room && (this.room.state === 'connected' || this.room.state === 'reconnecting')) {
      return from(null);
    }
    this.room = room;
    this.currentUserTracks.setId(this.currentUserId);
    this.currentUserTracks.setRole(this.currentUserRole);
    this.currentUserTracks.setMediaWrapper(this.currentUserMediaWrapper);

    this.currentUserTracks.setAudioTrack(room.localParticipant.audioTracks);
    this.currentUserTracks.setVideoTrack(room.localParticipant.videoTracks);
    this.notifyLocalVideoProvidedChanged();
    this.notifyLocalAudioProvidedChanged();

    this.setupLocalVoiceMeter();

    setInterval(() => {
      Object.keys(this.remoteUsersMedia).forEach((userId: string): void => {
        if (
          this.remoteParticipantsTrackById[userId]?.audio?.trackPublication?.track?.mediaStreamTrack &&
          this.studentsIndividualEventEmittersById$[userId]?.voiceMeterLevelChanged
        ) {
          const remoteUserMedia = this.remoteUsersMedia[userId];
          this.emmitVoiceLevel(
            remoteUserMedia.source,
            remoteUserMedia.analyzer,
            remoteUserMedia.stream,
            this.studentsIndividualEventEmittersById$[userId].voiceMeterLevelChanged
          );
        }
      });
    }, 100);

    this.registerRoomEventListeners(room);
    /* connect WebRTC users which are already in room */
    room.participants.forEach((participant: RemoteParticipant) => {
      this.onParticipantConnected(participant);
    });

    const roomConnection: RoomConnection = new RoomConnection(true, this.currentUserTracks.audio.isProvided, this.currentUserTracks.video.isProvided);

    return of(roomConnection);
  }

  public get isConnected(): boolean {
    return this.room && this.room.state === 'connected';
  }

  public get isReconnecting(): boolean {
    return this.room && this.room.state === 'reconnecting';
  }

  public emmitVoiceLevel(mediaStreamSource: MediaStreamAudioSourceNode, analyser: AnalyserNode, stream: MediaStream, eventEmitter: EventEmitter<number>): void {
    const samples: Uint8Array = new Uint8Array(analyser.frequencyBinCount);

    const track: MediaStreamTrack = stream.getTracks()[0];
    const level: number = null;
    if (track.readyState !== 'ended') {
      this.checkLevel(analyser, samples, level, eventEmitter);
    }
  }

  public onUserSpeak(userId: number): EventEmitter<SpeakingEventPayload> {
    const eventEmitter: EventEmitter<SpeakingEventPayload> = new EventEmitter();

    let speakStartTime: number = null;
    let speakEndTime: number = null;
    this.studentsIndividualEventEmittersById$[userId]?.voiceMeterLevelChanged?.subscribe((level: number): void => {
      /* level is number from zero to 1 */
      if (level > 0.2) {
        speakEndTime = null;
        if (speakStartTime === null) {
          speakStartTime = Date.now();
          eventEmitter.emit({state: 'start', timestamp: speakStartTime});
        }
      } else {
        if (speakEndTime === null) {
          /* this is the moment the user actually stopped talking - the 5s below is only a
           * confirmation delay before we trust it, it must not be added to the reported time */
          speakEndTime = Date.now();
        }

        if (!speakStartTime) {
          return;
        }

        if (Date.now() - speakEndTime > 5000) {
          eventEmitter.emit({state: 'end', timestamp: speakEndTime});
          speakStartTime = null;
          speakEndTime = null;
        }
      }
    });
    return eventEmitter;
  }

  //When an event happens in a room, these listeners react to it, and do the logic described here accordingly to the events
  private registerRoomEventListeners(room: Room): void {
    room.on('reconnected', () => {
      const roomConnection: RoomConnection = new RoomConnection(true, this.currentUserTracks.audio.isProvided, this.currentUserTracks.video.isProvided);

      this.onConnectedEventEmitter.emit(roomConnection);
    });

    room.on('disconnected', (disconnectedRoom, error) => {
      this.room.disconnect();
      this.clearServiceState();

      this.userDisconnectionHandlerService.onDisconnect.emit(Service.WEB_RTC);

      if (
        (error && error.message !== 'Participant disconnected because of duplicate identity') ||
        this.shouldReconnect
      ) {
        this.connectToRoom(this.token, this.currentUserId, this.roomName, this.currentUserRole).subscribe();
      }
    });

    room.on('participantConnected', (participant: RemoteParticipant) => {
      this.onParticipantConnected(participant);
    });
    room.on('participantDisconnected', (participant: RemoteParticipant) => {
      this.onParticipantDisconnecting(participant);
    });

    if (this.currentUserRole === AccessType.TEACHER || this.currentUserRole === AccessType.SUPPORT) {
      room.on('dominantSpeakerChanged', (participant) => {
        this.onDominantSpeakerChanged(participant);
      });
    }
  }

  private registerParticipantEvents(participant: RemoteParticipant): void {
    this.onParticipantTrackSubscribed(participant);
    this.onParticipantTrackUnsubscribed(participant);

    const userId: number = this.getUserIdByIdentity(participant.identity);
    const remoteUser = this.remoteUsers[userId];
    // TODO - need to move to wrtc services
    if ((this.currentUserRole === AccessType.TEACHER || this.currentUserRole === AccessType.SUPPORT) &&
      remoteUser.accessType === AccessType.STUDENT) {
      participant.on('networkQualityLevelChanged', (networkQualityLevel) => {
        this.onNetworkQualityLevelChanged(networkQualityLevel, userId);
      });
    }
  }

  private onParticipantConnected(participant: RemoteParticipant): void {
    const userId: number = this.getUserIdByIdentity(participant.identity);
    const remoteUser = this.remoteUsers[userId];

    if (remoteUser) {
      if(this.currentUserRole !== AccessType.STUDENT && remoteUser.accessType === AccessType.STUDENT){
        this.jsonLogService.writeJsonLogs(JsonLogAction.CONN_TO_TEACHER,{userId, studentName: remoteUser.name}).subscribe();
      }
      LogService.writeLog(`Participant connected event received. name: ${userId} tracks length ${participant.tracks ? participant.tracks.size : '0'}`, LogLevel.INFO, LogResource.WRTC);

      if (!this.remoteParticipantsTrackById[userId]) {
        const remoteUserTracksInfo = new RemoteUserTracks();
        remoteUserTracksInfo.setUserId(userId);
        remoteUserTracksInfo.setAccessType(remoteUser.accessType);
        this.remoteParticipantsTrackById[userId] = remoteUserTracksInfo;
      }
      this.remoteParticipantConnectionStateChanged$.emit({userId, isConnected: true});
      this.registerParticipantEvents(participant);
    } else {
      LogService.writeLog(`Participant with id ${participant.identity} does not exist session`, LogLevel.WARN, LogResource.WRTC);
    }

  }

  //Disconnecting participant from a room
  private onParticipantDisconnecting(participant: RemoteParticipant): void {
    const userId: number = this.getUserIdByIdentity(participant.identity);

    LogService.writeLog(`Participant disconnected event received. name: ${userId} tracks length ${participant.tracks ? participant.tracks.size : '0'}`, LogLevel.INFO, LogResource.WRTC);
    this.remoteParticipantConnectionStateChanged$.emit({userId, isConnected: false});
    const remoteUser = this.remoteUsers[userId];

    if (!remoteUser) {
      return;
    }

    if(this.currentUserRole !== AccessType.STUDENT && remoteUser.accessType === AccessType.STUDENT){
      this.jsonLogService.writeJsonLogs(JsonLogAction.DISCONN_FROM_TEACHER,{userId, studentName: remoteUser.name}).subscribe();
    }
    if (this.remoteParticipantsTrackById[userId]) {
      this.remoteParticipantsTrackById[userId].removeAudioTrack();
      this.remoteParticipantsTrackById[userId].removeVideoTrack();
    }
  }

  //Getting participants video and audio tracks
  private onParticipantTrackSubscribed(participant: RemoteParticipant): void {
    const userId: number = this.getUserIdByIdentity(participant.identity);
    participant.on('trackSubscribed', (track, trackPublication) => {
      this.onTrackReceived(trackPublication, userId);
    });
  }

  //Removing participants video and audio tracks
  private onParticipantTrackUnsubscribed(participant: RemoteParticipant): void {
    const userId: number = this.getUserIdByIdentity(participant.identity);

    participant.on('trackUnsubscribed', (track, trackPublication) => {
      this.onTrackDisconnected(trackPublication, userId);
    });
  }

  public shareScreen(): void {
    window.open('https://google.com', '200x200');
    navigator.mediaDevices.getDisplayMedia().then(
      stream => {
        // let a: LocalVideoTrack = createLocalVideoTrack(stream.getTracks()[0]);
      // let screenTrack = new LocalVideoTrack(stream.getTracks()[0]);
      this.room.localParticipant.publishTrack(stream.getTracks()[0]).then(r => console.log('Ready')); //after "then" I don't know what  argument to give
    }).catch(() => {
      alert('Could not share the screen.');
    }
    );
  };

  private getPublisherOptions(accessType: AccessType, cancelNoise: boolean = true): CreateLocalTracksOptions {
    const options: CreateLocalTracksOptions = {};
    if (accessType === AccessType.OBSERVER ) {
      options.audio = null;
      options.video = null;
    } else {
      options.video = {
        width: this.getCurrentUserVideoWidth(),
        height: this.getCurrentUserVideoHeight(),
        frameRate: this.getCurrentUserVideoFrameRate()
      };
      options.audio = {defaultDeviceCaptureMode: 'auto'};

      if (cancelNoise) {
        options.audio = {
          noiseCancellationOptions: {
            vendor: 'krisp',
            sdkAssetsPath: 'app/js-lib/krisp-audio-plugin/dist'
          }
        };
      }

      const videoDeviceId = localStorage.getItem('video_device_id');
      if (videoDeviceId) {
        options.video.deviceId = {exact: videoDeviceId};
      }

      const audioDeviceId = localStorage.getItem('audio_device_id');
      if (audioDeviceId) {
        options.audio.deviceId = {exact: audioDeviceId};
      }

      if (accessType === AccessType.SUPPORT) {
        options.video = null;
      }
    }

    return options;
  }

  public setCurrentUserVideoFrameRate(frameRate: number): void {
    this.currentUserVideoTrackOptions.frameRate = frameRate;
  }

  public setCurrentUserVideoResolution(resolution: string): void {
    switch (resolution) {
      case '176×144':
        this.currentUserVideoTrackOptions.resolution = {width: 176, height: 144};
        break;
      case '240x180':
        this.currentUserVideoTrackOptions.resolution = {width: 240, height: 180};
        break;
      case '320x240':
        this.currentUserVideoTrackOptions.resolution = {width: 320, height: 240};
        break;
      case '640x480':
        this.currentUserVideoTrackOptions.resolution = {width: 640, height: 480};
        break;
    }
  }

  private getCurrentUserVideoHeight(): number {
    if (this.currentUserVideoTrackOptions && this.currentUserVideoTrackOptions.resolution && this.currentUserVideoTrackOptions.resolution.height) {
      return this.currentUserVideoTrackOptions.resolution.height;
    } else {
      return this.currentUserRole === AccessType.TEACHER ? 480 : 180;
    }
  }

  private getCurrentUserVideoWidth(): number {
    if (this.currentUserVideoTrackOptions && this.currentUserVideoTrackOptions.resolution && this.currentUserVideoTrackOptions.resolution.width) {
      return this.currentUserVideoTrackOptions.resolution.width;
    } else {
      return this.currentUserRole === AccessType.TEACHER ? 640 : 240;
    }
  }

  private getCurrentUserVideoFrameRate(): number {
    if (this.currentUserVideoTrackOptions && this.currentUserVideoTrackOptions.frameRate) {
      return this.currentUserVideoTrackOptions.frameRate;
    } else {
      return this.currentUserRole === AccessType.TEACHER ? 24 : 12;
    }
  }

  private onNetworkQualityLevelChanged(level: number, id: number):  void {
    this.studentsIndividualEventEmittersById$[id].networkLevelChanged.emit({userId: id, level});
  }

  private onTrackReceived(trackPublication: TrackPublication, id: number): void {
    LogService.writeLog(`Received user - ${id} track of kind ${trackPublication.kind} event`, LogLevel.DEBUG, LogResource.WRTC);
    if (this.remoteParticipantsTrackById[id]) {
      switch (trackPublication.kind) {
        case TrackKind.AUDIO: {
          const doSubscribe = !this.classroom.isRoomMutedBecauseOfCurrentUserBlockedMicAndCam;
          const audioTrackPublication = (trackPublication as RemoteAudioTrackPublication);
          this.remoteParticipantsTrackById[id].setAudioTrack(audioTrackPublication, doSubscribe, this.selectedSpeakerDeviceId);

          const mediaStream: MediaStream = new MediaStream([this.remoteParticipantsTrackById[id].audio.trackPublication.track.mediaStreamTrack]);

          const remoteUserAudioContext: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
          const remoteUserAnalyzer: AnalyserNode = remoteUserAudioContext.createAnalyser();
          remoteUserAnalyzer.fftSize = 1024;
          remoteUserAnalyzer.smoothingTimeConstant = 0.5;

          remoteUserAudioContext.resume().then(() => {
            this.remoteUsersMedia[id] = {
              stream: mediaStream,
              source: remoteUserAudioContext.createMediaStreamSource(mediaStream),
              audioContext: remoteUserAudioContext,
              analyzer: remoteUserAnalyzer
            };
            this.remoteUsersMedia[id].source.connect(remoteUserAnalyzer);
          });

          this.audioTrackReceived$.emit({userId: id, isMuted: false});
          break;
        }
        case TrackKind.VIDEO: {
          const videoTrackPublication = (trackPublication as RemoteVideoTrackPublication);
          this.remoteParticipantsTrackById[id].setVideoTrack(videoTrackPublication);
          this.videoTrackReceived$.emit({userId: id, isMuted: false});
          break;
        }
        case TrackKind.DATA: {
          const dataTrackPublication: RemoteDataTrackPublication = (trackPublication as RemoteDataTrackPublication);

          dataTrackPublication.track.on('message', () => {
            LogService.writeLog(`Message received from TWILIO DATA API`, LogLevel.DEBUG, LogResource.WRTC);
          });
          break;
        }
      }
    } else {
      LogService.writeLog(`Remote participant info ${id} does not exist yet.`, LogLevel.WARN, LogResource.WRTC);
    }
  }

  private onTrackDisconnected(trackPublication: TrackPublication, id: number): void {
    LogService.writeLog(`Received user - ${id} track of kind ${trackPublication.kind} disconnected event`, LogLevel.DEBUG, LogResource.WRTC);
    switch (trackPublication.kind) {
      case TrackKind.AUDIO: {
        this.remoteParticipantsTrackById[id].removeAudioTrack();
        this.audioTrackReceived$.emit({userId: id, isMuted: true});
        break;
      }
      case TrackKind.VIDEO: {
        this.remoteParticipantsTrackById[id].removeVideoTrack();
        this.videoTrackReceived$.emit({userId: id, isMuted: true});
        break;
      }
    }
  }

  public getLocalUserAudioTrack(role: AccessType, enableNoiseCancellation: boolean): Observable<LocalAudioTrack> {
    return this.getAudioTrack(this.getPublisherOptions(role, enableNoiseCancellation).audio);
  }

  public getLocalUserVideoTrack(role: AccessType): Observable<LocalVideoTrack> {
    return this.getVideoTrack(this.getPublisherOptions(role).video);
  }

  public setParticipantVoiceLevel(userId: number, voiceLevel: number): void {
    if (this.remoteParticipantsTrackById[userId]) {
      this.remoteParticipantsTrackById[userId].setAudioLevel(voiceLevel);
    }
  }

  private checkLevel(analyser: AnalyserNode, samples: Uint8Array, level: number, eventEmitter: EventEmitter<any>): void {
    analyser.getByteFrequencyData(samples);
    const rms: number = TwilioService.rootMeanSquare(samples);
    const log2Rms: number = rms && Math.log(rms) / Math.log(2);

    const newLevel: number = Math.ceil(100 * log2Rms / 8);
    if (level !== newLevel) {
      level = (newLevel - 57) / 30;
      /* limit 'level' from 0 to 1 */
      level = Math.min(1, Math.max(0, level));
      eventEmitter.emit(level);
    }
  }

  private static rootMeanSquare(samples): number {
    const sumSq: number = samples.reduce((sumSqa, sample) => sumSqa + sample * sample, 0);
    return Math.sqrt(sumSq / samples.length);
  }

  public toggleDisableSelfAudio(isMuted: boolean): void {
    this.currentUserTracks.toggleDisableAudio(isMuted);
  }

  public toggleDisableSelfVideo(isMuted: boolean): void {
    this.currentUserTracks.toggleDisableVideo(isMuted);
  }

  public setSubscribeToAudioTrack(id: number, subscribe: boolean): void {
    const remoteParticipant: RemoteUserTracks = this.remoteParticipantsTrackById[id];
    if (remoteParticipant.audio.isLocalUserSubscribed !== subscribe && !this.classroom.isRoomMutedBecauseOfCurrentUserBlockedMicAndCam) {
      remoteParticipant.subscribeToAudio(subscribe);
      LogService.writeLog(`Local user subscription state to user ${id} changed to ${subscribe}`, LogLevel.DEBUG, LogResource.WRTC);
    }
  }

  public toggleSubscribeToVideoTrack(id: number, subscribe: boolean): void {
    const remoteParticipant: RemoteUserTracks = this.remoteParticipantsTrackById[id];
    remoteParticipant.subscribeToVideo(subscribe);
  }

  private getConnectOptions(roomName: string, accessType: AccessType): Observable<ConnectOptions> {
    return this.getTracks(this.publisherOptions).pipe(
      concatMap((localTracks) => {
        const connectionOptions: ConnectOptions = {
          automaticSubscription: true,
          maxAudioBitrate: 16000,
          bandwidthProfile: {
            video: {
              trackSwitchOffMode: 'disabled',
            }
          },
          name: roomName,
          tracks: localTracks,
          preferredAudioCodecs: [{codec: 'opus', dtx: false}]
        };

        if (accessType === AccessType.TEACHER || accessType === AccessType.SUPPORT) {
          connectionOptions.networkQuality = {local: 0, remote: 3};
          connectionOptions.dominantSpeaker = true;
        }

        if (accessType === AccessType.TEACHER) {
          connectionOptions.preferredVideoCodecs = [{codec: 'VP8', simulcast: true}];
          connectionOptions.bandwidthProfile = {video: {mode: 'grid', maxTracks: 21}};
          connectionOptions.maxVideoBitrate = 600000;
        } else {
          connectionOptions.preferredVideoCodecs = [{codec: 'VP8', simulcast: false}];
          connectionOptions.bandwidthProfile = {video: {mode: 'collaboration', maxTracks: 21}};
          connectionOptions.maxVideoBitrate = 400000;
        }
        this.store.dispatch(new ClassroomAction.ToggleLocalTracksFetched(true));
        return of(connectionOptions);
      }));
  }

  private getTracks(options: CreateLocalTracksOptions): Observable<LocalTrack []> {
    const tracks: LocalTrack [] = [];
    tracks.push(new LocalDataTrack());
    this.currentUserTracks.setDataTrackA(tracks[0]);

    return this.getVideoTrack(options.video).pipe(
      concatMap((videoTrack) => {
        if (videoTrack != null) {
          tracks.push(videoTrack);
        }
        return this.getAudioTrack(options.audio).pipe(
          map(audioTrack => {

            if (audioTrack != null) {
              tracks.push(audioTrack);
            }

            return tracks;
          })
        );
      })
    );
  }

  private getVideoTrack(trackInfo): Observable<LocalVideoTrack> {
    if (!trackInfo) {
      return of(null);
    } else {
      if (trackInfo.deviceId && trackInfo.deviceId.exact) {
        return this.getVideoDeviceById(trackInfo).pipe(catchError((error) => {
          LogService.writeLog(`Fail to get video track whit id ${trackInfo.deviceId.exact} try get video track without id. ${error?.name}: ${error?.message}`, LogLevel.WARN, LogResource.WRTC);
          delete trackInfo.deviceId;
          return from(createLocalVideoTrack(trackInfo)).pipe(catchError((fallbackError) => {
            LogService.writeLog(`Fail to get video track. ${fallbackError?.name}: ${fallbackError?.message}`, LogLevel.WARN, LogResource.WRTC);
            return of(null);
          }));
        }));
      } else {
        return from(createLocalVideoTrack(trackInfo)).pipe(catchError((error) => {
          LogService.writeLog(`Fail to get video track. ${error?.name}: ${error?.message}`, LogLevel.WARN, LogResource.WRTC);
          return of(null);
        }));
      }
    }
  }

  public getVideoDeviceById(trackInfo): Observable<LocalVideoTrack> {
    return from(createLocalVideoTrack(trackInfo));
  }

  private getAudioTrack(trackInfo): Observable<LocalAudioTrack> {
    if (!trackInfo) {
      return of(null);
    } else {
      if (trackInfo.deviceId && trackInfo.deviceId.exact) {
        return this.getAudioDeviceById(trackInfo).pipe(catchError(() => {
          LogService.writeLog(`Fail to get audio track with id ${trackInfo.deviceId.exact} try get audio track without id`, LogLevel.WARN, LogResource.WRTC);
          delete trackInfo.deviceId;
          return from(createLocalAudioTrack(trackInfo))
            .pipe(catchError(() => {
              LogService.writeLog(`Fail to get audio track `, LogLevel.WARN, LogResource.WRTC);
              return of(null);
            }));
        }));
      } else {
        return from(createLocalAudioTrack(trackInfo)).pipe(catchError(() => {
          LogService.writeLog(`Fail to get audio track `, LogLevel.WARN, LogResource.WRTC);
          return of(null);
        }));
      }
    }
  }

  public getAudioDeviceById(trackInfo): Observable<LocalAudioTrack> {
    return from(createLocalAudioTrack(trackInfo));
  }

  public async getVideoAndAudioDevices(): Promise<{ audio: DeviceMetadata[]; video: DeviceMetadata[]; audioOutput: DeviceMetadata[] }> {
    try {
      /* force a user to select audio device access */
      const _ = await navigator.mediaDevices.getUserMedia({audio: true});
    } catch (e) {
      console.warn(e);
    }
    try {
      /* force a user to select video device access */
      const _ = await navigator.mediaDevices.getUserMedia({video: true});
    } catch (e) {
      console.warn(e);
    }

    const mediaDevices = await navigator.mediaDevices.enumerateDevices();

    return mediaDevices.reduce(
      async (accumulatorPromise, mediaDevice) => {
        const accumulator = await accumulatorPromise;
        switch (mediaDevice.kind) {
          case 'videoinput': {
            let active: boolean = false;
            try {
              this.loadedTracksByDeviceId[mediaDevice.deviceId] = await this.getVideoDeviceById({deviceId: {exact: mediaDevice.deviceId}}).toPromise();
              active = true;
            } catch (e) {}

            accumulator.video.push(DeviceMetadata.fromMediaDeviceInfo(mediaDevice, active));
            break;
          }
          case 'audioinput': {
            let active: boolean = false;
            try {
              this.loadedTracksByDeviceId[mediaDevice.deviceId] = await this.getAudioDeviceById({deviceId: {exact: mediaDevice.deviceId}}).toPromise();
              active = true;
            } catch (e) {}

            accumulator.audio.push(DeviceMetadata.fromMediaDeviceInfo(mediaDevice, active));
            break;
          }
          case 'audiooutput': {
            accumulator.audioOutput.push(DeviceMetadata.fromMediaDeviceInfo(mediaDevice, mediaDevice.deviceId !== ''));
            break;
          }
        }
        return accumulatorPromise;
      },
      Promise.resolve({audio: [], video: [], audioOutput: []})
    );

    // let getTracksCount = 0;
    // let mediaFetchTryCount = 0;
    // const fetchedAudioDeviceGroupIds = [];
    // const audioVideoDevicesMetadata: { audio: DeviceMetadata[]; video: DeviceMetadata[]; audioOutput: DeviceMetadata[] } = {video: [], audio: [], audioOutput: []};

    // return new Observable(subscriber => {
    //   from(navigator.mediaDevices.enumerateDevices())
    //     .subscribe(mediaDevices => {
    //       mediaDevices.forEach(mediaDevice => {
    //         if (mediaDevice.kind === 'videoinput') {
    //           mediaFetchTryCount++;
    //           this.getVideoDeviceById({deviceId: {exact: mediaDevice.deviceId}})
    //             .subscribe(videoTrack => {
    //               LogService.writeGetTrackLog(videoTrack.id, mediaDevice.deviceId, 'video');
    //               getTracksCount++;
    //               audioVideoDevicesMetadata.video.push({
    //                 id: mediaDevice.deviceId,
    //                 groupId: mediaDevice.groupId,
    //                 label: mediaDevice.label,
    //                 active: true
    //               });
    //               this.loadedTracksByDeviceId[mediaDevice.deviceId] = videoTrack;
    //
    //               if (getTracksCount === mediaFetchTryCount) {
    //                 subscriber.next(audioVideoDevicesMetadata);
    //                 subscriber.complete();
    //               }
    //             }, () => {
    //               getTracksCount++;
    //               LogService.writeLog(`Can not get video track from device ${mediaDevice.deviceId}`, LogLevel.DEBUG, LogResource.WRTC);
    //               audioVideoDevicesMetadata.video.push({
    //                 id: mediaDevice.deviceId,
    //                 groupId: mediaDevice.groupId,
    //                 label: mediaDevice.label,
    //                 active: false
    //               });
    //
    //               if (getTracksCount === mediaFetchTryCount) {
    //                 subscriber.next(audioVideoDevicesMetadata);
    //                 subscriber.complete();
    //               }
    //             });
    //         } else if (mediaDevice.kind === 'audioinput' && fetchedAudioDeviceGroupIds.indexOf(mediaDevice.groupId) === -1) {
    //           mediaFetchTryCount++;
    //           fetchedAudioDeviceGroupIds.push(mediaDevice.groupId);
    //
    //           this.getAudioDeviceById({deviceId: {exact: mediaDevice.deviceId}}).subscribe(audioTrack => {
    //             LogService.writeGetTrackLog(audioTrack.id, mediaDevice.deviceId, 'audio');
    //             getTracksCount++;
    //             audioVideoDevicesMetadata.audio.push({
    //               id: mediaDevice.deviceId,
    //               groupId: mediaDevice.groupId,
    //               label: mediaDevice.label,
    //               active: true
    //             });
    //             this.loadedTracksByDeviceId[mediaDevice.deviceId] = audioTrack;
    //
    //             if (getTracksCount === mediaFetchTryCount) {
    //               subscriber.next(audioVideoDevicesMetadata);
    //               subscriber.complete();
    //             }
    //           }, () => {
    //             LogService.writeLog(`Can not get audio track from device ${mediaDevice.deviceId}`, LogLevel.DEBUG, LogResource.WRTC);
    //             getTracksCount++;
    //             audioVideoDevicesMetadata.audio.push({
    //               id: mediaDevice.deviceId,
    //               label: mediaDevice.label,
    //               groupId: mediaDevice.groupId,
    //               active: false
    //             });
    //             if (getTracksCount === mediaFetchTryCount) {
    //               subscriber.next(audioVideoDevicesMetadata);
    //               subscriber.complete();
    //             }
    //           });
    //         } else if (mediaDevice.kind === 'audiooutput') {
    //           mediaFetchTryCount++;
    //           getTracksCount++;
    //           audioVideoDevicesMetadata.audioOutput.push({
    //             id: mediaDevice.deviceId,
    //             groupId: mediaDevice.groupId,
    //             label: mediaDevice.label,
    //             active: true
    //           });
    //           subscriber.next(audioVideoDevicesMetadata);
    //           subscriber.complete();
    //         }
    //       });
    //     });
    // });
  }

  public loadAndAttachVideoTrackById(currentDeviceId, videoMediaWrapper): Observable<string> {
    return this.getVideoDeviceById({deviceId: {exact: currentDeviceId}}).pipe(
      catchError(err => {
        return throwError(err);
      }),
      concatMap(track => {
        LogService.writeGetTrackLog(track.id, currentDeviceId, 'video');
        videoMediaWrapper.innerHTML = '';
        // console.log(`try to attach track with id ${track.id}`);
        const videoElement: HTMLMediaElement = track.attach();

        videoMediaWrapper.appendChild(videoElement);
        videoElement.play().then();

        this.loadedTracksByDeviceId[currentDeviceId] = track;
        return of(track.id);
      }));
  }

  public detachAndStopVideoTrack(deviceId, doDetach): void {
    if (this.loadedTracksByDeviceId[deviceId]) {
      LogService.writeStopTrackLog(this.loadedTracksByDeviceId[deviceId].id, deviceId, 'video');

      if (doDetach) {
        this.loadedTracksByDeviceId[deviceId].detach();
      }

      this.loadedTracksByDeviceId[deviceId].stop();
      delete this.loadedTracksByDeviceId[deviceId];
    }
  }

  public stopAudioTrack(deviceId): void {
    if (this.loadedTracksByDeviceId[deviceId]) {
      LogService.writeStopTrackLog(this.loadedTracksByDeviceId[deviceId].id, deviceId, 'audio');
      this.loadedTracksByDeviceId[deviceId].stop();
      delete this.loadedTracksByDeviceId[deviceId];
    }
  }

  public updateLocalDevice(currentDeviceId: string): void {
    this.getAudioDeviceById({deviceId: {exact: currentDeviceId}}).subscribe((track: LocalAudioTrack) => {
      this.loadedTracksByDeviceId[currentDeviceId] = track;
    });
  }

  public setSpeakerDevice(currentDeviceId: string): void {
    this.selectedSpeakerDeviceId = currentDeviceId;
  }

  public setCurrentUserMediaWrapper(wrapper: HTMLElement): void {
    this.currentUserMediaWrapper = wrapper;
  }

  public addRemoteParticipantInfo(id: number, accessType: AccessType, wrapper: HTMLElement): void {
    if (accessType === AccessType.STUDENT) {
      this.studentsIndividualEventEmittersById$[id] = {
        networkLevelChanged: new EventEmitter<NetworkQualityLevelChangedPayload>(),
        voiceMeterLevelChanged: new EventEmitter<number>()
      };
    }

    if (this.remoteParticipantsTrackById[id]) {
      this.remoteParticipantsTrackById[id].setMediaWrapper(wrapper);
    } else {
      const remoteUserTracksInfo = new RemoteUserTracks();
      remoteUserTracksInfo.setUserId(id);
      remoteUserTracksInfo.setAccessType(accessType);
      remoteUserTracksInfo.setMediaWrapper(wrapper);
      this.remoteParticipantsTrackById[id] = remoteUserTracksInfo;
    }
  }

  public removeRemoteParticipantMediaWrappers(id: number): void {
    this.remoteParticipantsTrackById[id].removeMediaWrapper();
  }

  private onDominantSpeakerChanged(participant: RemoteParticipant): void {
    const userId: number = this.getUserIdByIdentity(participant?.identity);

    this.dominantSpeakerChanged$.emit(userId);
  }

  public getParticipantConnectedEventEmitter(): EventEmitter<RemoteParticipantConnectionEventPayload> {
    return this.remoteParticipantConnectionStateChanged$;
  }

  public getDominantSpeakerChangedEventEmitter(): EventEmitter<number> {
    return this.dominantSpeakerChanged$;
  }

  public getAudioTrackReceivedEventEmitter(): EventEmitter<TrackReceivedEventPayload> {
    return this.audioTrackReceived$;
  }

  public getVideoTrackReceivedEventEmitter(): EventEmitter<TrackReceivedEventPayload> {
    return this.videoTrackReceived$;
  }

  public getLocalVideoProvidedChangedEventEmitter(): EventEmitter<boolean> {
    return this.localVideoProvidedChanged$;
  }

  /* must be called after every mutation of the local video track, so camera availability is reported
   * from a single place instead of only on connect. Emits on real transitions only, so a reconnect
   * that leaves the camera in the same state stays silent */
  private notifyLocalVideoProvidedChanged(): void {
    const isProvided: boolean = !!this.currentUserTracks?.video?.isProvided;

    if (this.lastReportedLocalVideoProvided === isProvided) {
      return;
    }

    this.lastReportedLocalVideoProvided = isProvided;
    this.localVideoProvidedChanged$.emit(isProvided);
  }

  public getLocalAudioProvidedChangedEventEmitter(): EventEmitter<boolean> {
    return this.localAudioProvidedChanged$;
  }

  /* microphone counterpart of notifyLocalVideoProvidedChanged - must be called after every mutation
   * of the local audio track */
  private notifyLocalAudioProvidedChanged(): void {
    const isProvided: boolean = !!this.currentUserTracks?.audio?.isProvided;

    if (this.lastReportedLocalAudioProvided === isProvided) {
      return;
    }

    this.lastReportedLocalAudioProvided = isProvided;
    this.localAudioProvidedChanged$.emit(isProvided);
  }

  /* (re)builds the local voice level meter. Must be called after every change of the published audio
   * track: the meter keeps the MediaStream in a closure, so after a device change the old stream is
   * 'ended', no levels are emitted any more and speaking detection silently stops working */
  private setupLocalVoiceMeter(): void {
    this.stopLocalVoiceMeter();

    if (!this.currentUserTracks.audio.isProvided) {
      return;
    }

    if (!this.studentsIndividualEventEmittersById$[this.currentUserId]) {
      this.studentsIndividualEventEmittersById$[this.currentUserId] = {
        voiceMeterLevelChanged: new EventEmitter<number>(),
        networkLevelChanged: null
      };
    }

    const localUserAudioContext: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
    const localUserAnalyzer: AnalyserNode = localUserAudioContext.createAnalyser();
    localUserAnalyzer.fftSize = 1024;
    localUserAnalyzer.smoothingTimeConstant = 0.5;

    this.localVoiceMeterAudioContext = localUserAudioContext;

    localUserAudioContext.resume().then(() => {
      /* another meter was started while this one was resuming */
      if (this.localVoiceMeterAudioContext !== localUserAudioContext) {
        return;
      }

      if (!this.currentUserTracks.audio.trackPublication) {
        return;
      }

      const mediaStream: MediaStream = new MediaStream([this.currentUserTracks.audio.trackPublication.track.mediaStreamTrack]);
      const mediaStreamSource: MediaStreamAudioSourceNode = localUserAudioContext.createMediaStreamSource(mediaStream);
      mediaStreamSource.connect(localUserAnalyzer);

      this.localVoiceMeterIntervalRef = setInterval(() => {
        if (this.currentUserTracks?.audio?.trackPublication?.track?.mediaStreamTrack && this.studentsIndividualEventEmittersById$[this.currentUserId]?.voiceMeterLevelChanged) {
          this.emmitVoiceLevel(
            mediaStreamSource,
            localUserAnalyzer,
            mediaStream,
            this.studentsIndividualEventEmittersById$[this.currentUserId].voiceMeterLevelChanged
          );
        }
      }, 100);
    });
  }

  private stopLocalVoiceMeter(): void {
    if (this.localVoiceMeterIntervalRef) {
      clearInterval(this.localVoiceMeterIntervalRef);
      this.localVoiceMeterIntervalRef = null;
    }

    if (this.localVoiceMeterAudioContext) {
      this.localVoiceMeterAudioContext.close().catch(() => {});
      this.localVoiceMeterAudioContext = null;
    }
  }

  public detachLocalParticipantVideo(): void {
    this.currentUserTracks.detachVideoTrackAndRemoveWrapper();
  }

  public reattachLocalParticipantVideoTrack(): void {
    this.currentUserTracks.detachVideoTrackAndRemoveWrapper();
    this.currentUserTracks.attachVideoTrack();
  }

  public async republishLocalParticipantVideoTrack(deviceId?: string): Promise<void> {
    if (deviceId) {
      this.publisherOptions.video.deviceId = {exact: deviceId};
    }

    return new Promise(resolve => {
      this.getVideoTrack(this.publisherOptions.video).subscribe(track => {
        const tracks = Array.from(this.room.localParticipant.videoTracks.values());

        if (tracks && tracks.length > 0) {
          if (tracks[0].track) {
            LogService.writeStopTrackLog(tracks[0].track.id, null, 'video');
            tracks[0].track.stop();
          } else {
            LogService.writeLog('Try to stop null video stream', LogLevel.WARN, LogResource.WRTC);
          }

          this.room.localParticipant.unpublishTracks([tracks[0].track]);
          this.detachLocalParticipantVideo();
        }

        if (!track) {
          /* camera could not be acquired - the previous track is already stopped and unpublished,
           * so the user really has no video now */
          this.currentUserTracks.setVideoTrack(null);
          this.notifyLocalVideoProvidedChanged();
          resolve();
          return;
        }

        from(this.room.localParticipant.publishTrack(track))
          .subscribe(trackPublication => {
            const publicationMap = new Map<string, LocalTrackPublication>();
            publicationMap.set(trackPublication.trackSid, trackPublication);
            // TODO - need to consider if user has been muted how to handle it
            this.currentUserTracks.setVideoTrack(publicationMap);
            this.notifyLocalVideoProvidedChanged();
            resolve();
          });
      });
    });
  }

  public async republishLocalParticipantAudioTrack(deviceId?: string): Promise<void> {
    if (deviceId) {
      this.publisherOptions.audio.deviceId = {exact: deviceId};
    }

    return new Promise(resolve => {
      this.getAudioTrack(this.publisherOptions.audio).subscribe(track => {
        const currentTrack = Array.from(this.room.localParticipant.audioTracks.values());

        if (currentTrack && currentTrack.length > 0) {
          this.room.localParticipant.unpublishTracks([currentTrack[0].track]);
          currentTrack.forEach(item => {
            LogService.writeStopTrackLog(item.track.id, null, 'audio');
            item.track.stop();
          });
          this.currentUserTracks.audio.trackPublication.track.detach();
        }

        if (!track) {
          /* microphone could not be acquired - the previous track is already stopped and unpublished,
           * so the user really has no audio now */
          this.currentUserTracks.setAudioTrack(null);
          this.notifyLocalAudioProvidedChanged();
          this.setupLocalVoiceMeter();
          resolve();
          return;
        }

        from(this.room.localParticipant.publishTrack(track)).subscribe((trackPublication: LocalAudioTrackPublication) => {
          const publicationMap: Map<Track.SID, LocalAudioTrackPublication> = new Map<Track.SID, LocalAudioTrackPublication>();
          publicationMap.set(trackPublication.trackSid, trackPublication);
          this.currentUserTracks.setAudioTrack(publicationMap);
          this.notifyLocalAudioProvidedChanged();
          trackPublication.track.restart().then(() => {
            /* rebuild the meter only after restart(), so it binds to the final MediaStreamTrack */
            this.setupLocalVoiceMeter();
            resolve();
          });
        });
      });
    });
  }

  public getVideoTrackByDeviceId(deviceId: string): Promise<LocalVideoTrack> {
    return createLocalVideoTrack({deviceId: {exact: deviceId}});
  }

  public getAudioTrackByDeviceId(deviceId: string): Promise<LocalAudioTrack> {
    return createLocalAudioTrack({deviceId: {exact: deviceId}});
  }

  private getUserIdByIdentity(identity: string): number | null {
    let userId: number | null = null;
    for (const [id, user] of Object.entries(this.remoteUsers)) {
      if (user.identity === identity) {
        userId = user.id;
        break;
      }
    }
    return userId;
  }
}
